import dayjs from 'dayjs'

export const SPARTACUS_TABLE_SORT_FIELDS = {
  venueName: 'venueName',
  ordersInPeriod: 'ordersInPeriod',
  orderTrend: 'orderTrendPct',
  upgradeScore: 'upgradeScore',
  riskScore: 'riskScore',
  createdAt: 'createdAt',
  lastOrderedAt: 'lastOrderedAt',
}

export function mapSpartacusVenueRow(venue) {
  return {
    key: venue.venueId,
    ...venue,
  }
}

export function mapSpartacusVenuesResponse(response) {
  const venues = Array.isArray(response?.venues) ? response.venues : []

  return {
    venues: venues.map(mapSpartacusVenueRow),
    pagination: {
      page: response?.pagination?.page || 1,
      pageSize: response?.pagination?.pageSize || 25,
      total: response?.pagination?.total || 0,
    },
    meta: response?.meta || null,
  }
}

export function mapSpartacusDataResponse(response) {
  const venues = mapSpartacusVenuesResponse({
    venues: response?.venues,
    pagination: response?.pagination,
    meta: response?.meta,
  })

  return {
    ...venues,
    stats: mapSpartacusStatsResponse({
      ...response?.stats,
      meta: response?.meta,
    }),
  }
}

export function mapSpartacusStatsResponse(response) {
  if (!response) {
    return null
  }

  const { meta, ...stats } = response

  return {
    ...stats,
    meta: meta || null,
  }
}

export function formatSpartacusUkShortDate(value) {
  if (!value) {
    return '—'
  }

  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('D MMM YYYY') : '—'
}

export function formatOrderTrendDisplay(venue) {
  const label = venue?.orderTrendLabelDisplay || venue?.orderTrendLabel || '—'

  if (
    venue?.orderTrendLabel === 'new' ||
    venue?.orderTrendLabel === 'no_prior' ||
    venue?.orderTrendPct == null
  ) {
    return label
  }

  const prefix = venue.orderTrendPct > 0 ? '+' : ''
  return `${label} (${prefix}${venue.orderTrendPct}%)`
}

export function getOrderTrendTone(orderTrendLabel) {
  if (orderTrendLabel === 'up') {
    return 'positive'
  }
  if (orderTrendLabel === 'down') {
    return 'negative'
  }
  if (orderTrendLabel === 'flat') {
    return 'neutral'
  }
  return 'muted'
}

export function formatPercentValue(value, digits = 1) {
  if (value == null || Number.isNaN(Number(value))) {
    return '—'
  }
  return `${Number(value).toFixed(digits)}%`
}

export function formatSignedPercentDelta(value, digits = 1) {
  if (value == null || Number.isNaN(Number(value))) {
    return null
  }

  const numeric = Number(value)
  const prefix = numeric > 0 ? '+' : ''
  return `${prefix}${numeric.toFixed(digits)}%`
}

export function formatSignedPointsDelta(value, digits = 1) {
  if (value == null || Number.isNaN(Number(value))) {
    return null
  }

  const numeric = Number(value)
  const prefix = numeric > 0 ? '+' : ''
  return `${prefix}${numeric.toFixed(digits)} pts`
}

export function formatYesNo(value) {
  return value ? 'Yes' : 'No'
}

export function formatSpartacusPhoneLabel(phone) {
  const normalized = normalizeSpartacusPhone(phone)
  return normalized || '—'
}

export function getSpartacusPhoneHref(phone) {
  const normalized = normalizeSpartacusPhone(phone)
  if (!normalized) {
    return null
  }

  const dialable = normalized.replace(/[^\d+]/g, '')
  return dialable ? `tel:${dialable}` : null
}

function normalizeSpartacusPhone(phone) {
  const trimmed = String(phone || '').trim()
  if (!trimmed) {
    return ''
  }

  const dialable = trimmed.replace(/[^\d+]/g, '')
  if (!dialable || /^0+$/.test(dialable)) {
    return ''
  }

  return trimmed
}

export function isSpartacusCountSignalActive(value) {
  return Number(value) > 0
}

export function isSpartacusYesNoSignalActive(value) {
  return value === true
}

export function isSpartacusGpPercentSignalActive(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return false
  }

  return Number(value) !== 0
}

export function getSpartacusUpgradeScoreTone(score) {
  const numeric = Number(score)

  if (!Number.isFinite(numeric)) {
    return 'muted'
  }

  if (numeric >= 70) {
    return 'positive'
  }

  if (numeric >= 40) {
    return 'warning'
  }

  return 'negative'
}

export function getSpartacusRiskScoreTone(score) {
  const numeric = Number(score)

  if (!Number.isFinite(numeric)) {
    return 'muted'
  }

  if (numeric >= 70) {
    return 'negative'
  }

  if (numeric >= 40) {
    return 'warning'
  }

  return 'positive'
}

export function getSpartacusActiveSuppliersTone(count) {
  const numeric = Number(count) || 0

  if (numeric >= 5) {
    return 'positive'
  }

  if (numeric >= 1) {
    return 'warning'
  }

  return 'negative'
}

export function formatSpartacusLastSeenLabel(label) {
  if (!label || label === 'Not tracked yet') {
    return 'Not tracked yet'
  }

  return formatSpartacusUkShortDate(label)
}

export function formatSpartacusVenueActivityLabel(label) {
  if (!label || label === 'Not available') {
    return 'Not available'
  }

  return formatSpartacusUkShortDate(label)
}

export function mapSpartacusVenueUsersResponse(response) {
  const users = Array.isArray(response?.users) ? response.users : []

  return {
    venueId: response?.venueId || null,
    venueName: response?.venueName || '',
    lastVenueActivity: response?.lastVenueActivity || null,
    users: users.map(user => ({
      key: user.accountId || user.email,
      ...user,
    })),
  }
}

/** Pinned notes first, then newest createdAt. Same order as the Spartacus query. */
export function sortSpartacusVenueNotes(notes = []) {
  const list = Array.isArray(notes) ? notes : []
  return [...list].sort((a, b) => {
    if (Boolean(a?.pinned) !== Boolean(b?.pinned)) {
      return a.pinned ? -1 : 1
    }
    return String(b?.createdAt || '').localeCompare(String(a?.createdAt || ''))
  })
}

export function mapSpartacusVenueNote(note) {
  if (!note) {
    return null
  }

  return {
    key: note.noteId,
    noteId: note.noteId,
    venueId: note.venueId,
    noteText: note.noteText || '',
    noteType: note.noteType || '',
    outcome: note.outcome || '',
    followUpAt: note.followUpAt || null,
    pinned: Boolean(note.pinned),
    author: {
      accountId: note.author?.accountId || '',
      name: note.author?.name || '',
      email: note.author?.email || '',
    },
    createdAt: note.createdAt || null,
    updatedAt: note.updatedAt || null,
  }
}

export function mapSpartacusVenueNotesResponse(response) {
  const notes = Array.isArray(response?.notes) ? response.notes : []

  return {
    venueId: response?.venueId || null,
    notes: notes.map(mapSpartacusVenueNote).filter(Boolean),
    pagination: {
      page: response?.pagination?.page || 1,
      pageSize: response?.pagination?.pageSize || 20,
      total: response?.pagination?.total || 0,
    },
  }
}

export function formatSpartacusNoteAuthorLabel(author = {}) {
  const name = String(author?.name || '').trim()
  const email = String(author?.email || '').trim()

  if (name && email) {
    return `${name} (${email})`
  }

  return name || email || 'Unknown author'
}

export function formatSpartacusCustomerNotesButtonLabel(notesCount = 0) {
  const count = Number(notesCount) || 0
  return `Customer notes (${count})`
}

export function hasUntrackedLastSeenUsers(users = []) {
  return users.some(user => user.lastSeenLabel === 'Not tracked yet')
}

export function mapTableSorterToSpartacusSort(sorter = {}) {
  const columnKey = sorter.columnKey || sorter.field
  const sortBy = SPARTACUS_TABLE_SORT_FIELDS[columnKey]

  if (!sortBy || !sorter.order) {
    return null
  }

  return {
    sortBy,
    sortDir: sorter.order === 'ascend' ? 'asc' : 'desc',
  }
}

export function getTableColumnSortOrder(columnKey, sortBy, sortDir) {
  if (SPARTACUS_TABLE_SORT_FIELDS[columnKey] !== sortBy) {
    return false
  }

  return sortDir === 'asc' ? 'ascend' : 'descend'
}
